/**
 * Referral attribution + code generation (SIS-307).
 *
 * Captures `?ref=XXX` URL params on app load and stashes them in localStorage
 * with a 30-day TTL, then surfaces the pending code at signup time so the
 * `recordReferralSignup` Cloud Function can record the attribution.
 */

const PENDING_REFERRAL_STORAGE_KEY = "showcase.pendingReferral";
const PENDING_REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// 30-char ambiguity-free alphabet (drops 0/O/1/I/L/U/V) — matches the backfill
// script. 8 chars × 30 alphabet ≈ 6.5×10¹¹ codes; collisions are vanishingly
// rare and handled by SetMyReferralCodeIfNull's isNull guard anyway.
const REFERRAL_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTWXYZ";
const REFERRAL_CODE_LENGTH = 8;

interface PendingReferral {
  code: string;
  capturedAt: number;
}

function safeStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Generates a cryptographically random 8-character referral slug.
 * Uses crypto.getRandomValues so the alphabet is sampled uniformly without
 * the modulo bias that Math.random()-based generators introduce.
 */
export function generateReferralCode(): string {
  const buffer = new Uint32Array(REFERRAL_CODE_LENGTH);
  crypto.getRandomValues(buffer);
  let code = "";
  for (let i = 0; i < REFERRAL_CODE_LENGTH; i++) {
    code += REFERRAL_CODE_ALPHABET[buffer[i] % REFERRAL_CODE_ALPHABET.length];
  }
  return code;
}

/**
 * If `?ref=XXX` is present in the current URL, store it in localStorage with
 * a fresh timestamp. Idempotent — safe to call on every load. Overwrites any
 * existing pending code (most-recent-link-wins, never stacks).
 */
export function captureReferralFromUrl(): void {
  const storage = safeStorage();
  if (!storage) return;

  const params = new URLSearchParams(window.location.search);
  const code = params.get("ref");
  if (!code) return;

  // Length sanity-check before persisting — guards against junk like "?ref=&"
  // or pathological long strings being shoved into localStorage.
  const trimmed = code.trim().toUpperCase();
  if (trimmed.length === 0 || trimmed.length > 12) return;

  const payload: PendingReferral = {
    code: trimmed,
    capturedAt: Date.now(),
  };

  try {
    storage.setItem(PENDING_REFERRAL_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota errors etc. — referral attribution is best-effort.
  }
}

/**
 * Returns the pending referral code if one was captured within the TTL window,
 * otherwise null. Stale entries are cleared as a side effect to keep storage
 * small and prevent ancient codes from being used at next login.
 */
export function readPendingReferralCode(): string | null {
  const storage = safeStorage();
  if (!storage) return null;

  const raw = storage.getItem(PENDING_REFERRAL_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PendingReferral>;
    if (!parsed.code || typeof parsed.capturedAt !== "number") {
      storage.removeItem(PENDING_REFERRAL_STORAGE_KEY);
      return null;
    }
    if (Date.now() - parsed.capturedAt > PENDING_REFERRAL_TTL_MS) {
      storage.removeItem(PENDING_REFERRAL_STORAGE_KEY);
      return null;
    }
    return parsed.code;
  } catch {
    storage.removeItem(PENDING_REFERRAL_STORAGE_KEY);
    return null;
  }
}

/**
 * Clears the pending referral code. Called after a successful
 * recordReferralSignup so the same code can't be re-applied if the user
 * later signs up another household member from the same browser.
 */
export function clearPendingReferralCode(): void {
  const storage = safeStorage();
  if (!storage) return;
  storage.removeItem(PENDING_REFERRAL_STORAGE_KEY);
}

/**
 * Builds the magic-link continuation URL with the pending referral code
 * appended, so that `?ref=XXX` round-trips through the email link and is
 * re-captured when the user lands back on the app in a different browser
 * session (where localStorage from the original tab isn't visible).
 */
export function appendReferralToContinueUrl(baseUrl: string): string {
  const pending = readPendingReferralCode();
  if (!pending) return baseUrl;
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}ref=${encodeURIComponent(pending)}`;
}

/**
 * Builds the public app URL a user should share to attribute new signups to
 * their referral code. Uses the current origin so previews/staging share the
 * matching environment's URL.
 */
export function buildReferralShareUrl(code: string): string {
  if (typeof window === "undefined") {
    return `https://app.getshowcase.ai/?ref=${encodeURIComponent(code)}`;
  }
  return `${window.location.origin}/?ref=${encodeURIComponent(code)}`;
}
